/**
 * Справочник типов бизнеса.
 * Используется в: Диагностика маркетинга, Калькулятор масштабирования,
 * в будущем — «Консультация · Часть 2».
 */

const DEFAULT_BENCHMARKS = {
  ltvCacGood: 3,
  ltvCacWarn: 2,
  repeatShareWarn: 85,
  budgetShareMin: 5,
  budgetShareMax: 15,
};

export const BUSINESS_NICHE_DIRECTORY = {
  local_service: {
    id: 'local_service',
    order: 1,
    label: 'Локальный сервис',
    desc: 'CPL растёт умеренно при масштабе',
    examplesKey: 'NICHE_LOCAL_SERVICE_EX',
    cplFactors: { 1: 1, 2: 1.12, 3: 1.28, 5: 1.55 },
    diagnosticDefaults: {
      leads: 80,
      conv: 31,
      sales: 120,
      ltvQ: 8,
      churn: 5,
      revenue: 1800000,
      checkDepth: 1,
      margin: 65,
      cacShare: 30,
      budget: 250000,
      revenueTarget: 2500000,
    },
    diagnosticBenchmarks: {
      ...DEFAULT_BENCHMARKS,
      repeatShareWarn: 90,
      budgetShareMin: 4,
      budgetShareMax: 12,
    },
    consultationPart2: {
      planTemplateKey: 'PART2_LOCAL_SERVICE',
      priorityLevers: ['l', 'q', 'c', 'p'],
      channelHints: ['geo', 'meta', '2gis'],
    },
  },
  online_services: {
    id: 'online_services',
    order: 2,
    label: 'Онлайн-услуги',
    desc: 'Эксперт продаёт время и знания',
    examplesKey: 'NICHE_ONLINE_SERVICES_EX',
    cplFactors: { 1: 1, 2: 1.15, 3: 1.32, 5: 1.62 },
    diagnosticDefaults: {
      leads: 30,
      conv: 27,
      sales: 23,
      ltvQ: 4,
      churn: 5,
      revenue: 1150000,
      checkDepth: 1,
      margin: 75,
      cacShare: 30,
      budget: 200000,
      revenueTarget: 1800000,
    },
    diagnosticBenchmarks: { ...DEFAULT_BENCHMARKS, budgetShareMax: 18 },
    consultationPart2: {
      planTemplateKey: 'PART2_ONLINE_SERVICES',
      priorityLevers: ['c', 'p', 'l', 'q'],
      channelHints: ['meta', 'content', 'referral'],
    },
  },
  retail_showroom: {
    id: 'retail_showroom',
    order: 3,
    label: 'Розничный магазин / шоурум',
    desc: 'Офлайн или омниканал',
    examplesKey: 'NICHE_RETAIL_EX',
    cplFactors: { 1: 1, 2: 1.16, 3: 1.35, 5: 1.68 },
    diagnosticDefaults: {
      leads: 200,
      conv: 20,
      sales: 120,
      ltvQ: 3,
      churn: 5,
      revenue: 4200000,
      checkDepth: 1.2,
      margin: 45,
      cacShare: 25,
      budget: 400000,
      revenueTarget: 5500000,
    },
    diagnosticBenchmarks: {
      ...DEFAULT_BENCHMARKS,
      budgetShareMin: 3,
      budgetShareMax: 10,
    },
    consultationPart2: {
      planTemplateKey: 'PART2_RETAIL',
      priorityLevers: ['c', 'p', 'l', 'q'],
      channelHints: ['meta', 'marketplace', 'geo'],
    },
  },
  ecommerce: {
    id: 'ecommerce',
    order: 4,
    label: 'Интернет-магазин',
    desc: 'Чистый e-commerce',
    examplesKey: 'NICHE_ECOMMERCE_EX',
    cplFactors: { 1: 1, 2: 1.18, 3: 1.38, 5: 1.75 },
    diagnosticDefaults: {
      leads: 500,
      conv: 5,
      sales: 85,
      ltvQ: 2.5,
      churn: 5,
      revenue: 1020000,
      checkDepth: 1.5,
      margin: 35,
      cacShare: 25,
      budget: 600000,
      revenueTarget: 1500000,
    },
    diagnosticBenchmarks: {
      ...DEFAULT_BENCHMARKS,
      ltvCacGood: 2.5,
      budgetShareMin: 8,
      budgetShareMax: 25,
    },
    consultationPart2: {
      planTemplateKey: 'PART2_ECOMMERCE',
      priorityLevers: ['l', 'c', 'p', 'q'],
      channelHints: ['meta', 'google', 'marketplace'],
    },
  },
  b2b_services: {
    id: 'b2b_services',
    order: 5,
    label: 'B2B услуги',
    desc: 'CPL растёт заметнее при масштабе',
    examplesKey: 'NICHE_B2B_EX',
    cplFactors: { 1: 1, 2: 1.22, 3: 1.45, 5: 1.9 },
    diagnosticDefaults: {
      leads: 25,
      conv: 20,
      sales: 13,
      ltvQ: 6,
      churn: 5,
      revenue: 6500000,
      checkDepth: 1,
      margin: 55,
      cacShare: 35,
      budget: 350000,
      revenueTarget: 9000000,
    },
    diagnosticBenchmarks: {
      ...DEFAULT_BENCHMARKS,
      ltvCacGood: 4,
      ltvCacWarn: 2.5,
      budgetShareMax: 12,
    },
    consultationPart2: {
      planTemplateKey: 'PART2_B2B',
      priorityLevers: ['c', 'p', 'l', 'q'],
      channelHints: ['linkedin', 'outbound', 'partnerships'],
    },
  },
  production: {
    id: 'production',
    order: 6,
    label: 'Производство / продукт',
    desc: 'Своё производство или дистрибуция',
    examplesKey: 'NICHE_PRODUCTION_EX',
    cplFactors: { 1: 1, 2: 1.2, 3: 1.4, 5: 1.8 },
    diagnosticDefaults: {
      leads: 15,
      conv: 27,
      sales: 16,
      ltvQ: 5,
      churn: 5,
      revenue: 12800000,
      checkDepth: 1,
      margin: 40,
      cacShare: 30,
      budget: 500000,
      revenueTarget: 17000000,
    },
    diagnosticBenchmarks: {
      ...DEFAULT_BENCHMARKS,
      ltvCacGood: 4,
      budgetShareMin: 2,
      budgetShareMax: 8,
    },
    consultationPart2: {
      planTemplateKey: 'PART2_PRODUCTION',
      priorityLevers: ['p', 'q', 'c', 'l'],
      channelHints: ['b2b', 'trade', 'partnerships'],
    },
  },
  education: {
    id: 'education',
    order: 7,
    label: 'Образование и экспертный контент',
    desc: 'Длинный цикл принятия решения',
    examplesKey: 'NICHE_EDUCATION_EX',
    cplFactors: { 1: 1, 2: 1.14, 3: 1.3, 5: 1.58 },
    diagnosticDefaults: {
      leads: 60,
      conv: 20,
      sales: 42,
      ltvQ: 3,
      churn: 5,
      revenue: 3360000,
      checkDepth: 1,
      margin: 60,
      cacShare: 30,
      budget: 300000,
      revenueTarget: 5000000,
    },
    diagnosticBenchmarks: {
      ...DEFAULT_BENCHMARKS,
      repeatShareWarn: 80,
      budgetShareMax: 20,
    },
    consultationPart2: {
      planTemplateKey: 'PART2_EDUCATION',
      priorityLevers: ['l', 'c', 'q', 'p'],
      channelHints: ['meta', 'content', 'webinar'],
    },
  },
  real_estate: {
    id: 'real_estate',
    order: 8,
    label: 'Недвижимость и строительство',
    desc: 'Высокий CPL и сильный рост при масштабе',
    examplesKey: 'NICHE_REAL_ESTATE_EX',
    cplFactors: { 1: 1, 2: 1.25, 3: 1.5, 5: 2.0 },
    diagnosticDefaults: {
      leads: 20,
      conv: 10,
      sales: 3,
      ltvQ: 1.5,
      churn: 5,
      revenue: 6000000,
      checkDepth: 1,
      margin: 50,
      cacShare: 35,
      budget: 800000,
      revenueTarget: 9000000,
    },
    diagnosticBenchmarks: {
      ...DEFAULT_BENCHMARKS,
      ltvCacGood: 5,
      ltvCacWarn: 3,
      repeatShareWarn: 70,
      budgetShareMin: 3,
      budgetShareMax: 10,
    },
    consultationPart2: {
      planTemplateKey: 'PART2_REAL_ESTATE',
      priorityLevers: ['c', 'l', 'p', 'q'],
      channelHints: ['meta', 'classifieds', 'partnerships'],
    },
  },
};

export const BUSINESS_NICHE_LIST = Object.values(BUSINESS_NICHE_DIRECTORY).sort(
  (a, b) => a.order - b.order,
);

/** Профили для калькулятора масштабирования (обратная совместимость). */
export const NICHE_PROFILES = Object.fromEntries(
  BUSINESS_NICHE_LIST.map((profile) => [
    profile.id,
    {
      labelKey: profile.labelKey,
      descKey: profile.descKey,
      examplesKey: profile.examplesKey,
      cplFactors: profile.cplFactors,
    },
  ]),
);

export const getNicheProfile = (nicheId) => BUSINESS_NICHE_DIRECTORY[nicheId] || null;

export const isValidNicheId = (nicheId) => Boolean(getNicheProfile(nicheId));

export const getDiagnosticDefaultsForNiche = (nicheId) => {
  const profile = getNicheProfile(nicheId);
  if (!profile) return null;
  return { ...profile.diagnosticDefaults };
};

export const getDiagnosticBenchmarks = (nicheId) => {
  const profile = getNicheProfile(nicheId);
  return profile?.diagnosticBenchmarks || DEFAULT_BENCHMARKS;
};
